import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import RadioSelection from 'v2/components/Shared/RadioSelection';
import { sendEventToGA } from 'v2/helpers/analytics';
import { EAnticipationStatus } from 'v2/interfaces/user';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import styles from './styles';

export type AnticipationData = {
  credit_days: number;
  has_promotional_fee: boolean;
  custom_fee: {
    value: number;
  };
};

interface ICardListProps {
  open: boolean;
  setClose: () => void;
  anticipationData: AnticipationData;
  loadAnticipationData: () => void;
}

interface IWarningAnticipationBlockedProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
}

interface IWarningPromotionInclusionProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  hasPromotion: boolean;
  promotionValue: number;
}

interface IWarningCommonAnticipationChangeProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  currentPeriod: number;
  hasPromotion: boolean;
  hasPromotionalFee: boolean;
}

interface IWarningLeavingAnticipationPromotionProps {
  isAnticipationBlocked: boolean;
  warningClass: string;
  period: number;
  currentPeriod: number;
  hasPromotion: boolean;
  hasPromotionalFee: boolean;
  promotionalFee: number;
}

const periodOptions = [
  {
    value: '2',
    label: '2 dias',
  },
  {
    value: '7',
    label: '7 dias',
  },
  {
    value: '14',
    label: '14 dias',
  },
  {
    value: '21',
    label: '21 dias',
  },
  {
    value: '30',
    label: '30 dias (padrão)',
  },
];

function WarningAnticipationBlocked({ isAnticipationBlocked, warningClass }: IWarningAnticipationBlockedProps) {
  if (isAnticipationBlocked) {
    return (
      <Alert severity='error' className={warningClass}>
        Antecipação bloqueada por alto índice de chargeback
      </Alert>
    );
  }
  return null;
}

function WarningPromotionInclusion({ isAnticipationBlocked, warningClass, period, hasPromotion, promotionValue }: IWarningPromotionInclusionProps) {
  if (!isAnticipationBlocked && period === 2 && hasPromotion) {
    return (
      <Alert severity='warning' className={warningClass}>
        Você está aderindo a taxa <b>promocional</b> de antecipação de 2 dias, que é {promotionValue}% + 0.1% por dia antecipado, e passa a receber os
        valores de acordo com o novo prazo configurado. Deseja continuar?{' '}
        <b>Essa promoção é válida até o dia 30/11/2022. Caso confirme, a taxa promocional terá vigência até 31/12/2022.</b>
      </Alert>
    );
  }
  return null;
}

function WarningCommonAnticipationChange({
  isAnticipationBlocked,
  warningClass,
  period,
  currentPeriod,
  hasPromotion,
  hasPromotionalFee,
}: IWarningCommonAnticipationChangeProps) {
  if (!isAnticipationBlocked && period !== currentPeriod && !hasPromotionalFee && !(hasPromotion && period === 2)) {
    return (
      <Alert severity='warning' className={warningClass}>
        Os ganhos futuros com data de crédito superior a {period} dias serão creditados em até 24h. Deseja realmente continuar?
      </Alert>
    );
  }
  return null;
}

function WarningLeavingAnticipationPromotion({
  isAnticipationBlocked,
  warningClass,
  period,
  currentPeriod,
  hasPromotion,
  hasPromotionalFee,
  promotionalFee,
}: IWarningLeavingAnticipationPromotionProps) {
  if (!isAnticipationBlocked && period !== currentPeriod && hasPromotionalFee && !(hasPromotion && period === 2)) {
    return (
      <Alert severity='warning' className={warningClass}>
        Ao continuar, você deixa de ter direito à taxa promocional no valor de {promotionalFee}% e passa a receber os valores de acordo com o novo
        prazo configurado e taxa comum de 2.99% + 0.1% por dia antecipado. Deseja continuar?
      </Alert>
    );
  }
  return null;
}

function AnticipationPeriodModal({ open, setClose, anticipationData, loadAnticipationData }: ICardListProps) {
  const [period, setPeriod] = useState<number>(30);
  const [anticipationPromotion, setAnticipationPromotion] = useState(null);
  const [loadingChangePeriod, setLoadingChangePeriod] = useState(false);

  const classes = styles();

  const handleChangePeriod = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Antecipação automática',
      label: `${value} dias`,
    });

    setPeriod(parseInt(value));
  };

  const handleCloseButton = () => {
    setClose();
    if (anticipationData?.credit_days) {
      setPeriod(anticipationData.credit_days);
    }
  };

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const isAnticipationBlocked = user?.anticipation_status === EAnticipationStatus.BLOQUEADO;

  const [loadAnticipationPromotion] = useCallbackObservable(() => {
    return financialStatementService.getAnticipationPromotion().pipe(
      tap(({ data }) => {
        setAnticipationPromotion(data);
      }),
      catchError((error) => {
        toast.error(error);
        return of(true);
      })
    );
  }, []);

  useEffect(() => {
    loadAnticipationPromotion();
    if (anticipationData?.credit_days) {
      setPeriod(anticipationData.credit_days);
    }
  }, [loadAnticipationPromotion, anticipationData]);

  const [changePeriod] = useCallbackObservable(() => {
    setLoadingChangePeriod(true);
    if (period !== anticipationData?.credit_days) {
      return financialStatementService.setAnticipationPeriod(period).pipe(
        tap(
          () => {
            setLoadingChangePeriod(false);
            loadAnticipationData();
            setClose();
            toast.success(`Alteração realizada com sucesso! Sua antecipação automática está configurada para ${period} dias após crédito em conta.`);
          },
          (err) => {
            setLoadingChangePeriod(false);
            toast.error(err);
            setPeriod(anticipationData?.credit_days);
          }
        )
      );
    } else {
      setLoadingChangePeriod(false);
      setClose();
      toast.error('Opção selecionada já é o período de antecipação automática atual.');
      return null;
    }
  }, [period]);

  const currentFee = anticipationData?.has_promotional_fee ? `${anticipationData?.custom_fee?.value}%` : '2.99%';

  return (
    <Modal
      open={open}
      setClose={handleCloseButton}
      textTitle='Antecipação automática'
      textReturnButton='Cancelar'
      textProceedButton={loadingChangePeriod ? 'Carregando...' : 'Alterar'}
      handleProceedClick={changePeriod}
      isProceedButtonDisabled={isAnticipationBlocked}
    >
      <RadioSelection
        formLabel='Programe em quantos dias deseja receber seus ganhos.'
        options={periodOptions}
        selectedValue={period.toString()}
        handleSelected={handleChangePeriod}
        isDisabled={isAnticipationBlocked}
      />
      <Box className={classes.observationText}>
        * O recebimento antes do prazo incorre tarifa administrativa de {currentFee} + 0.1% por dia antecipado.{' '}
        <a
          href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
          target='_blank'
          rel='noopener noreferrer'
        >
          Saiba mais.
        </a>
      </Box>
      <WarningAnticipationBlocked isAnticipationBlocked={isAnticipationBlocked} warningClass={classes.warningAlert} />
      <WarningPromotionInclusion
        isAnticipationBlocked={isAnticipationBlocked}
        warningClass={classes.warningAlert}
        period={period}
        hasPromotion={anticipationPromotion?.isActive}
        promotionValue={anticipationPromotion?.value}
      />
      <WarningCommonAnticipationChange
        isAnticipationBlocked={isAnticipationBlocked}
        warningClass={classes.warningAlert}
        period={period}
        currentPeriod={anticipationData?.credit_days}
        hasPromotion={anticipationPromotion?.isActive}
        hasPromotionalFee={anticipationData?.has_promotional_fee}
      />
      <WarningLeavingAnticipationPromotion
        isAnticipationBlocked={isAnticipationBlocked}
        warningClass={classes.warningAlert}
        period={period}
        currentPeriod={anticipationData?.credit_days}
        hasPromotion={anticipationPromotion?.isActive}
        hasPromotionalFee={anticipationData?.has_promotional_fee}
        promotionalFee={anticipationData?.custom_fee?.value}
      />
    </Modal>
  );
}

export default AnticipationPeriodModal;
